import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { MotionConfig } from 'framer-motion';

import { ensureAuthBootstrapped } from '@/features/auth/api/bootstrap';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { NotFoundState, RouteErrorState } from '@/shared/components/RouteStates';
import { Toaster } from '@/shared/components/Toaster';

import '@/app.css';

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  // Rehydrate the session from the refresh cookie ONCE before any child guard
  // runs, so a page refresh doesn't redirect an authenticated user to login.
  // Memoised + error-swallowing, so it's a no-op on later navigations.
  beforeLoad: () => ensureAuthBootstrapped(),
  component: RootComponent,
  notFoundComponent: NotFoundState,
  errorComponent: RouteErrorState,
});

function RootComponent() {
  const queryClient = Route.useRouteContext({
    select: (context) => context.queryClient,
  });
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {/* reducedMotion="user" makes every framer-motion animation honor
            the OS setting automatically (transform/layout degrade to none). */}
        <MotionConfig reducedMotion="user">
          <Outlet />
          <Toaster />
        </MotionConfig>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
